import { ApiError, errorMessage } from "@api/errors";
import {
  geneSearchCarriers,
  geneSearchMolecular,
  geneSearchOffspring,
  geneSearchParents,
  geneSearchPlaces,
  geneSearchTrait,
  geneSearchTraitRows,
  getGeneSearchExportUrl,
  getGeneSearchTraitExportUrl,
} from "@api/generated/gene-search/gene-search";
import type {
  BankPlace,
  CarrierRow,
  LabCall,
  MarkerTraitDraft,
  OffspringRow,
  ParentRow,
  ParentSide,
  PlotPlace,
  SearchIn,
  TermIn,
  TermResult,
  TraitConditionIn,
  TraitGroup,
  TraitIn,
  TraitInOrder,
  TraitRow,
} from "@api/generated/model";
import { useQueryCatalog } from "@api/generated/query/query";
import { apiDownloadJson } from "@api/http";
import { useAutosave, useSeasonFromLink, useSettings } from "@app/workspace";
import { keyColumn, LineLink, lineColumn } from "@features/genealogy/GenotypeParts";
import { keyText } from "@features/genealogy/genotype";
import { LineListCard, type LineListValue, lineListBody, lineListEmpty, pickedSchema } from "@features/genealogy/LineList";
import { sendable, traitFields } from "@features/genealogy/markerTrait";
import { type Combine, combineSchema, type Term, TermChips, termKey, termLabel, termSchema } from "@features/genealogy/TermChips";
import { TraitConditions } from "@features/genealogy/TraitConditions";
import { SavedQuerySelect, useSavedQuery } from "@features/queries/SavedQuerySelect";
import { hint } from "@i18n";
import { AnimatedTabsList } from "@ui/AnimatedTabsList";
import "@features/genealogy/genealogy.css";
import { Button, Loader, MultiSelect, SegmentedControl, Select, Tabs, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatNumber, isNumericKind, num } from "@ui/format";
import { notifyError } from "@ui/notify";
import { PageHeader } from "@ui/PageHeader";
import { codeColumn, codeText } from "@ui/table/columns";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { YearInput } from "@ui/YearInput";
import type { TFunction } from "i18next";
import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const TABS = ["carriers", "offspring", "parents", "places", "molecular", "trait"] as const;
type Tab = (typeof TABS)[number];
const STATS = ["n", "minimum", "q1", "median", "q3", "maximum", "mean"] as const;
const SCOPES = ["all", "field", "list"] as const;
type Scope = (typeof SCOPES)[number];
const TRAIT_SHOWN = 500; // the plot rows drawn (the download has them all)
const NO_TERMS: Term[] = [];

export const Route = createFileRoute("/genealogy/gene-search")({
  validateSearch: z.object({
    t: z.array(termSchema).optional(),
    c: combineSchema.optional(),
    scope: z.enum(SCOPES).optional(),
    from: z.number().int().optional(),
    to: z.number().int().optional(),
    tab: z.enum(TABS).optional(),
    ev: z.number().int().optional(), // scope "list": the genotype list (its trials' season)
    trials: z.array(z.string()).optional(),
    q: z.number().int().optional(),
    lines: z.array(pickedSchema).optional(),
  }),
  component: GeneSearchPage,
});

/** Genealógia → Génkereső (the legacy Gene Search and Marker-Trait associations): the lines that carry or lack the
 *  asked genes, alleles and markers (or a genotype list's every line), their offspring and parents, where they stood,
 *  their lab results, and a trait by a gene's presence. */
function GeneSearchPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const season = useSeasonFromLink(search.ev);
  if (!season) return <Loader size="sm" m="md" />;
  const terms: Term[] = search.t ?? NO_TERMS;
  const scope = search.scope ?? "all";
  const combine = search.c ?? "all";
  const years: [number, number] = [search.from ?? season - 5, search.to ?? season];
  const listing = scope === "list";
  const ready = listing ? !lineListEmpty(search) : terms.length > 0;
  // the years matter to the lines only with scope "field" (and always to the places): not asked for again otherwise
  const lines: SearchIn | null = ready
    ? { terms, combine, scope, years: scope === "field" ? years : null, line_list: listing ? lineListBody(season, search) : null }
    : null;
  const places: SearchIn | null = lines && { ...lines, years };
  const tabs = TABS.filter((x) => x !== "trait" || terms.length > 0);
  const tab = tabs.find((x) => x === search.tab) ?? "carriers";
  const set = (patch: Partial<typeof search>) => void navigate({ search: (s) => ({ ...s, ...patch }) });
  return (
    <div className="page fill">
      <PageHeader title={t("geneSearch.title")} subtitle={t("geneSearch.subtitle")} season={listing} />
      <Conditions terms={terms} combine={combine} scope={scope} years={years} onChange={set} />
      {listing && (
        <div className="hstack" style={{ marginBottom: 14 }}>
          <LineListCard
            season={season}
            value={search}
            onChange={(patch: LineListValue) => set(patch)}
            title={t("geneSearch.lineListTitle")}
          />
        </div>
      )}
      {/* the result tabs appear with the first condition, or a list (before it they would have nothing to show) */}
      {!lines || !places ? (
        <>
          <p className="note sm">{t(listing ? "geneSearch.lineListEmpty" : "geneSearch.empty")}</p>
          {!listing && <TraitWithoutTerm season={season} />}
        </>
      ) : (
        <Tabs value={tab} onChange={(v) => v && set({ tab: v as Tab })} keepMounted={false}>
          <AnimatedTabsList mb="md">
            {tabs.map((x) => (
              <Tabs.Tab key={x} value={x}>
                {t(x === "carriers" && listing ? "geneSearch.tab.genotypeList" : `geneSearch.tab.${x}`)}
              </Tabs.Tab>
            ))}
          </AnimatedTabsList>
          <Tabs.Panel value="carriers">
            <CarriersTab body={lines} exported={places} season={season} />
          </Tabs.Panel>
          <Tabs.Panel value="offspring">
            <OffspringTab body={lines} exported={places} />
          </Tabs.Panel>
          <Tabs.Panel value="parents">
            <ParentsTab body={lines} exported={places} />
          </Tabs.Panel>
          <Tabs.Panel value="places">
            <PlacesTab body={places} />
          </Tabs.Panel>
          <Tabs.Panel value="molecular">
            <MolecularTab body={lines} exported={places} />
          </Tabs.Panel>
          <Tabs.Panel value="trait">
            <TraitTab terms={terms} season={season} />
          </Tabs.Panel>
        </Tabs>
      )}
    </div>
  );
}

/** No gene asked: the marker–trait view over every plot, each with all its line's lab results (the legacy showed all
 *  molecular data then); opened on request, as it reads every plot of the years. */
function TraitWithoutTerm({ season }: { season: number }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (open) return <TraitTab terms={NO_TERMS} season={season} />;
  return (
    <div className="hstack">
      <Button size="compact-sm" variant="default" onClick={() => setOpen(true)}>
        {t("geneSearch.traitWithoutTermOpen")}
      </Button>
    </div>
  );
}

/** The label of the lines that do not satisfy a term. */
function otherLabel(t: TFunction, term: TermIn) {
  if (term.want === "value") return t("geneSearch.otherValue", { name: term.name });
  return termLabel(t, { ...term, want: term.want === "absent" ? "present" : "absent" });
}

function Conditions(props: {
  terms: Term[];
  combine: Combine;
  scope: Scope;
  years: [number, number];
  onChange: (patch: { t?: Term[]; c?: Combine; scope?: Scope; from?: number; to?: number }) => void;
}) {
  const { t } = useTranslation();
  return (
    <section className="card pad gs-search">
      <div className="stack" style={{ gap: 6, minWidth: 0 }}>
        <span className="sec">{t("geneSearch.molecularCondition")}</span>
        <TermChips
          terms={props.terms}
          onChange={(terms) => props.onChange({ t: terms })}
          combine={props.combine}
          onCombine={(c) => props.onChange({ c })}
        />
      </div>
      <Select
        size="sm"
        w={220}
        label={t("geneSearch.scope")}
        value={props.scope}
        allowDeselect={false}
        onChange={(v) => v && props.onChange({ scope: v as Scope })}
        data={SCOPES.map((s) => ({ value: s, label: t(`geneSearch.scopes.${s}`) }))}
      />
      <YearRange key={props.years.join("-")} years={props.years} onChange={(from, to) => props.onChange({ from, to })} />
    </section>
  );
}

/** The years, taken when a field is left (not at every keystroke: 2, 20, 202 are no years to search). */
function YearRange(props: { years: [number, number]; onChange: (from: number, to: number) => void; help?: string; size?: "xs" | "sm" }) {
  const { t } = useTranslation();
  const { years, onChange } = props;
  const [from, setFrom] = useState<number | null>(years[0]);
  const [to, setTo] = useState<number | null>(years[1]);
  const commit = () => from != null && to != null && (from !== years[0] || to !== years[1]) && onChange(from, to);
  const field = (label: string, v: number | null, set: (n: number | null) => void, visible: boolean) => (
    <YearInput
      size={props.size ?? "sm"}
      label={visible ? label : undefined}
      aria-label={label}
      value={v ?? ""}
      onChange={(x) => set(num(x))}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && commit()}
    />
  );
  return (
    <div className="hstack" style={{ gap: 6, alignItems: "flex-end" }}>
      {field(t("geneSearch.years"), from, setFrom, true)}
      <span className="mu" style={{ paddingBottom: 8 }}>
        –
      </span>
      {field(t("geneSearch.yearsTo"), to, setTo, false)}
      {props.help !== "" && (
        <span className="sm mu" style={{ paddingBottom: 8, maxWidth: 220 }}>
          {props.help ?? t("geneSearch.yearsHelp")}
        </span>
      )}
    </div>
  );
}

/** A search endpoint's answer for the tab on show (asked again only when its search changes). */
function useSearchResult<R>(name: string, body: SearchIn, fetch: (b: SearchIn, signal: AbortSignal) => Promise<R>) {
  return useQuery({
    queryKey: ["gene-search", name, body],
    queryFn: ({ signal }) => fetch(body, signal),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/** Why a tab has no answer: the server's reason (an empty list, a query without lines …), else a retry hint. */
function failure(t: TFunction, error: unknown) {
  return error instanceof ApiError && error.status === 422 ? errorMessage(error) : t("geneSearch.error");
}

/** A line's result for a term, with the effect of the allele the lab found (the allele register's). */
function Mark({ result }: { result: TermResult }) {
  return (
    <span className={`chip ${result.status == null ? "y" : result.status ? "g" : ""}`} title={result.effect ?? undefined}>
      {result.value ?? "?"}
      {result.effect && <span className="mu"> · {result.effect}</span>}
    </span>
  );
}

/** A column per term (by its place: the first term's column stays the first term's when the search changes): the
 *  line's result with the allele's effect. */
function termColumns<T>(terms: TermIn[], results: (row: T) => TermResult[]): Column<T>[] {
  return terms.map((term, i) => ({
    id: `term${i + 1}`,
    header: term.want === "value" ? `${term.name} = ${term.value}` : term.name,
    value: (r) => results(r)[i]?.value ?? "?",
    text: (r) => {
      const x = results(r)[i];
      return x ? `${x.value ?? "?"} ${x.effect ?? ""}` : "";
    },
    cell: (r) => {
      const x = results(r)[i];
      return x && <Mark result={x} />;
    },
  }));
}

/** A download button (the .xlsx made on the server from the body). */
function Download(props: { url: string; body: unknown; file: string; label: string }) {
  const [busy, setBusy] = useState(false);
  const download = () => {
    setBusy(true);
    apiDownloadJson(props.url, props.body, props.file)
      .catch(notifyError)
      .finally(() => setBusy(false));
  };
  return (
    <Button size="compact-sm" variant="default" loading={busy} onClick={download}>
      {props.label}
    </Button>
  );
}

/** A tab's table in a card: its rows, a loader, an error, and the count with the whole search's .xlsx. */
function ResultCard(props: {
  query: { isFetching: boolean; error: unknown; data?: { truncated: boolean } };
  count: number;
  exported: SearchIn;
  head?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { query } = props;
  return (
    <section className="card stack" style={{ gap: 0, minWidth: 0 }}>
      {props.head}
      {props.children}
      {query.isFetching && !query.data && <Loader size="sm" m="md" />}
      {query.error != null && <p className="note warn sm inset">{failure(t, query.error)}</p>}
      {query.data && (
        <div className="hstack sm mu inset" style={{ gap: 10 }}>
          <span className="grow">
            {t("geneSearch.rowCount", { count: formatNumber(props.count) })}
            {query.data.truncated && ` · ${t("geneSearch.truncated")}`} · {t("geneSearch.questionMarkHelp")}
          </span>
          <Download
            url={getGeneSearchExportUrl()}
            body={props.exported}
            file={t("geneSearch.carriersFile")}
            label={t("geneSearch.excel")}
          />
        </div>
      )}
    </section>
  );
}

type Stand = { last_year: number | null; trials: string[]; genebank_kg: number | null };
const kg = (v: number | null) => (v != null ? `${formatNumber(v)} kg` : "—");

/** Where a line stands: its last year in the field with that year's trials, its seed in the gene bank. */
function standColumns<T extends Stand>(t: TFunction): Column<T>[] {
  return [
    { id: "last_year", header: t("geneSearch.lastYear"), value: (r) => r.last_year, text: (r) => codeText(r.last_year), numeric: true },
    {
      id: "trials",
      header: t("geneSearch.trial"),
      value: (r) => r.trials.join(", "),
      className: "mono sm gs-trials",
      cellProps: (r) => ({ title: r.trials.join(", ") }),
    },
    {
      id: "genebank_kg",
      header: t("geneSearch.genebank"),
      value: (r) => r.genebank_kg,
      text: (r) => kg(r.genebank_kg),
      numeric: true,
    },
  ];
}

function CarriersTab({ body, exported, season }: { body: SearchIn; exported: SearchIn; season: number }) {
  const { t } = useTranslation();
  const query = useSearchResult("carriers", body, (b, signal) => geneSearchCarriers(b, { signal }));
  const terms = body.terms ?? NO_TERMS;
  const columns = useMemo<Column<CarrierRow>[]>(
    () => [
      lineColumn("genotype", t("geneSearch.genotype"), (r) => r.line),
      keyColumn((r) => r.line.key),
      ...termColumns<CarrierRow>(terms, (r) => r.results),
      ...standColumns<CarrierRow>(t),
    ],
    [t, terms],
  );
  const table = useDataTable("genealogy.gene-search.carriers", columns, query.data?.rows, { partial: !!query.data?.truncated });
  const card = (
    <ResultCard query={query} count={table.total} exported={exported}>
      <DataTable table={table} rowKey={(r) => keyText(r.line.key)} />
    </ResultCard>
  );
  if (!terms.length) return card;
  return (
    <div className="gt-grid">
      {card}
      <QuickTrait terms={terms} season={season} />
    </div>
  );
}

function OffspringTab({ body, exported }: { body: SearchIn; exported: SearchIn }) {
  const { t } = useTranslation();
  const query = useSearchResult("offspring", body, (b, signal) => geneSearchOffspring(b, { signal }));
  const terms = body.terms ?? NO_TERMS;
  const columns = useMemo<Column<OffspringRow>[]>(
    () => [
      lineColumn("parent", t("geneSearch.parent"), (r) => r.parent),
      { id: "role", header: t("geneSearch.roleColumn"), value: (r) => t(`geneSearch.role.${r.role}`), className: "sm" },
      lineColumn("offspring", t("geneSearch.offspring"), (r) => r.line),
      keyColumn((r) => r.line.key),
      ...termColumns<OffspringRow>(terms, (r) => r.results),
      ...standColumns<OffspringRow>(t),
    ],
    [t, terms],
  );
  const table = useDataTable("genealogy.gene-search.offspring", columns, query.data?.rows, { partial: !!query.data?.truncated });
  return (
    <ResultCard query={query} count={table.total} exported={exported}>
      <DataTable table={table} rowKey={(r) => `${keyText(r.parent.key)}-${r.role}-${keyText(r.line.key)}`} />
    </ResultCard>
  );
}

/** A parent as its link reads (a name not in the register as written in the pedigree). */
const parentText = (p: ParentSide) => (p.line ? (p.line.name ?? p.text ?? keyText(p.line.key)) : (p.text ?? ""));

function ParentsTab({ body, exported }: { body: SearchIn; exported: SearchIn }) {
  const { t } = useTranslation();
  const query = useSearchResult("parents", body, (b, signal) => geneSearchParents(b, { signal }));
  const terms = body.terms ?? NO_TERMS;
  const columns = useMemo<Column<ParentRow>[]>(() => {
    const side = (id: "mother" | "father"): Column<ParentRow> => ({
      id,
      header: t(`geneSearch.role.${id}`),
      value: (r) => (r[id] ? parentText(r[id]) : null),
      text: (r) => {
        const p = r[id];
        return p ? [parentText(p), ...p.results.map((x) => `${x.value ?? "?"} ${x.effect ?? ""}`)].join(" ") : "";
      },
      cell: (r) => {
        const p = r[id];
        return p ? (
          <span className="hstack" style={{ gap: 6, flexWrap: "wrap" }}>
            <LineLink line={p.line} text={p.text} />
            {p.line ? (
              p.results.map((x, j) => <Mark key={termKey(terms[j] as TermIn)} result={x} />)
            ) : (
              <span className="chip y">{t("genotype.notInRegister")}</span>
            )}
          </span>
        ) : (
          <span className="mu">—</span>
        );
      },
    });
    return [
      lineColumn<ParentRow>("genotype", t("geneSearch.genotype"), (r) => r.line),
      ...termColumns<ParentRow>(terms, (r) => r.results),
      side("mother"),
      side("father"),
    ];
  }, [t, terms]);
  const table = useDataTable("genealogy.gene-search.parents", columns, query.data?.rows, { partial: !!query.data?.truncated });
  return (
    <ResultCard query={query} count={table.total} exported={exported}>
      <DataTable table={table} rowKey={(r) => keyText(r.line.key)} />
    </ResultCard>
  );
}

function PlacesTab({ body }: { body: SearchIn }) {
  const { t } = useTranslation();
  const query = useSearchResult("places", body, (b, signal) => geneSearchPlaces(b, { signal }));
  const [from, to] = body.years ?? [];
  const plotColumns = useMemo<Column<PlotPlace>[]>(
    () => [
      { id: "year", header: t("geneSearch.year"), value: (p) => p.ev, text: (p) => String(p.ev), numeric: true },
      { id: "trial", header: t("geneSearch.trial"), value: (p) => p.kis, className: "mono" },
      codeColumn("plot", t("geneSearch.plot"), (p) => p.parc),
      lineColumn("genotype", t("geneSearch.genotype"), (p) => ({ key: p.key, name: p.name ?? null })),
      keyColumn((p) => p.key),
    ],
    [t],
  );
  const bankColumns = useMemo<Column<BankPlace>[]>(
    () => [
      lineColumn("genotype", t("geneSearch.genotype"), (b) => ({ key: b.key, name: b.name ?? null })),
      { id: "trial", header: t("geneSearch.trial"), value: (b) => b.kis, className: "mono" },
      codeColumn("plot", t("geneSearch.plot"), (b) => b.parc),
      { id: "kg", header: "kg", value: (b) => b.suly, numeric: true },
    ],
    [t],
  );
  const partial = { partial: !!query.data?.truncated };
  const plots = useDataTable("genealogy.gene-search.places.plots", plotColumns, query.data?.plots, partial);
  const bank = useDataTable("genealogy.gene-search.places.genebank", bankColumns, query.data?.genebank, partial);
  const head = (title: string, n: number) => (
    <div className="hstack panel-head">
      <h2 className="h2 grow">{title}</h2>
      {query.data && <span className="chip">{formatNumber(n)}</span>}
    </div>
  );
  return (
    <div className="gt-grid">
      <ResultCard
        query={query}
        count={plots.total}
        exported={body}
        head={head(t("geneSearch.nursery", { from: from, to: to }), plots.total)}
      >
        <DataTable table={plots} rowKey={(p) => `${p.ev}-${p.kis}-${p.parc}`} />
      </ResultCard>
      <section className="card stack" style={{ gap: 0, minWidth: 0 }}>
        {head(t("geneSearch.genebankItems"), bank.total)}
        <DataTable table={bank} rowKey={(b) => b.id} />
      </section>
    </div>
  );
}

const call = (present: boolean | null) => (present == null ? "?" : present ? "YES" : "NO");

/** Every lab result of the lines (the legacy Show Molecular): of the searched genes, alleles and markers, or all. */
function MolecularTab({ body, exported }: { body: SearchIn; exported: SearchIn }) {
  const { t } = useTranslation();
  const query = useSearchResult("molecular", body, (b, signal) => geneSearchMolecular(b, { signal }));
  const columns = useMemo<Column<LabCall>[]>(() => {
    const plain = (id: "gene" | "allele" | "marker" | "effect"): Column<LabCall> => ({
      id,
      header: t(`geneSearch.molecularColumns.${id}`),
      value: (r) => r[id],
    });
    return [
      lineColumn<LabCall>("genotype", t("geneSearch.genotype"), (r) => r.line),
      keyColumn<LabCall>((r) => r.line.key),
      plain("gene"),
      plain("allele"),
      plain("marker"),
      {
        id: "result",
        header: t("geneSearch.molecularColumns.result"),
        value: (r) => call(r.present),
        cell: (r) => <span className={`chip ${r.present == null ? "y" : r.present ? "g" : ""}`}>{call(r.present)}</span>,
      },
      plain("effect"),
      { id: "samples", header: t("geneSearch.molecularColumns.samples"), value: (r) => r.samples, numeric: true },
    ];
  }, [t]);
  const table = useDataTable("genealogy.gene-search.molecular", columns, query.data?.rows, { partial: !!query.data?.truncated });
  return (
    <ResultCard query={query} count={table.total} exported={exported}>
      <DataTable table={table} rowKey={(r) => `${keyText(r.line.key)}|${r.gene}|${r.allele}|${r.marker}`} />
    </ResultCard>
  );
}

const TRAIT_DEFAULT: MarkerTraitDraft = {
  field: "trms.atlag",
  extra: [],
  years: null,
  site: "",
  query: null,
  order: "plot",
  conditions: [],
};

/** The Marker–tulajdonság tab: its choices (the trait, the Display fields, the years or a saved query, the phenotype
 *  conditions) are the user's draft ``marker_trait_draft``. */
function TraitTab({ terms, season }: { terms: TermIn[]; season: number }) {
  const settings = useSettings();
  if (!settings) return <Loader size="sm" m="md" />;
  return <StoredTrait terms={terms} season={season} stored={settings.marker_trait_draft ?? null} />;
}

function StoredTrait({ terms, season, stored }: { terms: TermIn[]; season: number; stored: MarkerTraitDraft | null }) {
  const [draft, setDraft] = useState<MarkerTraitDraft>(stored ?? TRAIT_DEFAULT);
  useAutosave("marker_trait_draft", draft, stored);
  return <TraitPanel terms={terms} season={season} draft={draft} onChange={(p) => setDraft((d) => ({ ...d, ...p }))} wide />;
}

/** The quick view beside the carriers (one year, one field): its choices are not kept. */
function QuickTrait({ terms, season }: { terms: TermIn[]; season: number }) {
  const [draft, setDraft] = useState<MarkerTraitDraft>(TRAIT_DEFAULT);
  return <TraitPanel terms={terms} season={season} draft={draft} onChange={(p) => setDraft((d) => ({ ...d, ...p }))} />;
}

/** Marker–tulajdonság: a trait's plot values grouped by whether the line satisfies a term, as box plots; in the wide
 *  panel (the tab) over a year range, a saved query's plots or its lines' plots of the years (a line query, e.g. of
 *  molecular results), chosen by the legacy phenotype conditions, with the
 *  Display fields and the plots row by row. */
function TraitPanel(props: {
  terms: TermIn[];
  season: number;
  draft: MarkerTraitDraft;
  onChange: (patch: Partial<MarkerTraitDraft>) => void;
  wide?: boolean;
}) {
  const { terms, season, draft, onChange, wide } = props;
  const { t } = useTranslation();
  const catalog = useQueryCatalog({ query: { staleTime: Number.POSITIVE_INFINITY } }).data;
  const [index, setIndex] = useState(0);
  const field = draft.field ?? null;
  const query = wide ? (draft.query ?? null) : null;
  const years: [number, number] = draft.years ?? [season, season];
  const [site] = useDebouncedValue((draft.site ?? "").trim(), 400);
  const [asked] = useDebouncedValue(years, 400); // not at every keystroke of a year
  const [conditions] = useDebouncedValue(JSON.stringify(wide ? sendable(draft.conditions) : []), 400);
  const term = terms[Math.min(index, terms.length - 1)];
  const displayable = useMemo(() => traitFields(catalog), [catalog]);
  const fields = useMemo(() => displayable.filter((f) => isNumericKind(f.kind)), [displayable]);
  // the groups depend on these; the plot rows also on the other terms, the Display fields and the order
  const base = field
    ? {
        term: term ?? null, // none: the plots are not grouped, each row lists its line's every lab result
        field,
        years: asked,
        site: site || null, // a plot query has its own; a line query's lines are read at it
        query,
        conditions: JSON.parse(conditions) as TraitConditionIn[],
      }
    : null;
  const body: TraitIn | null = base && { ...base, terms, extra: wide ? (draft.extra ?? []) : [], order: draft.order ?? "plot" };
  // saving the query in the builder asks again
  const saved = useSavedQuery(query);
  const stamp = query == null ? null : saved.stamp;
  const view = useQuery({
    queryKey: ["gene-search", "trait", base, stamp],
    queryFn: ({ signal }) => geneSearchTrait(body as TraitIn, { signal }),
    enabled: body != null && saved.ready,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });
  const label = (k: TraitGroup["klass"]) => {
    if (k === "all") return t("geneSearch.allPlots");
    if (!term) return "";
    return { match: termLabel(t, term), other: otherLabel(t, term), unknown: t("geneSearch.unknown") }[k];
  };
  return (
    <aside className={`card pad stack ${wide ? "" : "gs-side"}`} style={{ gap: 10 }}>
      <h2 className="h2">{t(wide ? "geneSearch.traitFullTitle" : "geneSearch.traitQuickTitle")}</h2>
      {!term && <p className="sm mu">{t("geneSearch.traitWithoutTerm")}</p>}
      <div className={wide ? "hstack" : "stack"} style={{ gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        {terms.length > 1 && (
          <Select
            size="xs"
            label={t("geneSearch.groupBy")}
            value={String(index)}
            allowDeselect={false}
            onChange={(v) => setIndex(Number(v))}
            data={terms.map((x, i) => ({ value: String(i), label: termLabel(t, x) }))}
          />
        )}
        <Select
          placeholder={hint("searchSelect")}
          size="xs"
          searchable
          label={t("geneSearch.trait")}
          value={field}
          onChange={(v) => onChange({ field: v })}
          data={fields.map((f) => ({ value: f.value, label: f.label }))}
          limit={100}
        />
        {wide ? (
          <>
            <SavedQuerySelect
              w={220}
              label={t("geneSearch.plotsSource")}
              description={t("geneSearch.plotsSourceHelp")}
              value={query}
              onChange={(id) => onChange({ query: id })}
            />
            <YearRange key={years.join("-")} years={years} size="xs" help="" onChange={(from, to) => onChange({ years: [from, to] })} />
          </>
        ) : (
          <YearInput
            size="xs"
            label={t("geneSearch.year")}
            value={years[0]}
            onChange={(v) => onChange({ years: [num(v) ?? season, num(v) ?? season] })}
          />
        )}
        <TextInput
          size="xs"
          w={90}
          label={t("geneSearch.site")}
          placeholder="MV"
          value={draft.site ?? ""}
          onChange={(e) => onChange({ site: e.currentTarget.value })}
        />
      </div>
      {wide && (
        <TraitConditions
          catalog={catalog}
          fields={displayable}
          value={draft.conditions ?? []}
          onChange={(value) => onChange({ conditions: value })}
        />
      )}
      {view.error != null && (
        <p className="note warn sm">
          {view.error instanceof ApiError && view.error.code === "unknown_field" ? t("geneSearch.notNumeric") : failure(t, view.error)}
        </p>
      )}
      {view.data && (
        <>
          <BoxPlot groups={view.data.groups} label={label} />
          {wide && <TraitTable groups={view.data.groups} label={label} />}
          {term && (
            <span className="sm mu">
              {view.data.p == null
                ? t("geneSearch.noTest")
                : t("geneSearch.testResult", { p: view.data.p < 0.001 ? "< 0,001" : `= ${formatNumber(view.data.p, 3)}` })}
            </span>
          )}
        </>
      )}
      {wide && body && view.data && (
        <TraitRows
          body={body}
          stamp={stamp}
          fields={displayable}
          extra={draft.extra ?? []}
          onExtra={(extra) => onChange({ extra })}
          order={draft.order ?? "plot"}
          onOrder={(order) => onChange({ order })}
          label={label}
        />
      )}
    </aside>
  );
}

/** A value of the plot table: a number in Hungarian notation, text as it is. */
function cell(v: number | string | null) {
  if (v == null) return "—";
  return typeof v === "number" ? formatNumber(v, 2) : v;
}

/** The trait's plots row by row (the legacy Marker-Trait grid): the plot, its line, the fields (the trait and the
 *  Display fields), every term's result and the plot's group; the Display fields, the order, and the .xlsx of every
 *  row. */
function TraitRows(props: {
  body: TraitIn;
  stamp: unknown;
  fields: { value: string; label: string }[];
  extra: string[];
  onExtra: (extra: string[]) => void;
  order: TraitInOrder;
  onOrder: (order: TraitInOrder) => void;
  label: (k: TraitGroup["klass"]) => string;
}) {
  const { t } = useTranslation();
  const rows = useQuery({
    queryKey: ["gene-search", "trait-rows", props.body, props.stamp],
    queryFn: ({ signal }) => geneSearchTraitRows(props.body, { limit: TRAIT_SHOWN }, { signal }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });
  const data = rows.data;
  const { fields, label } = props;
  const terms = props.body.terms ?? NO_TERMS;
  const columns = useMemo<Column<TraitRow>[]>(() => {
    const labels = new Map(fields.map((f) => [f.value, f.label]));
    return [
      { id: "year", header: t("geneSearch.year"), value: (r) => r.ev, text: (r) => String(r.ev), numeric: true },
      { id: "th", header: "TH", value: (r) => r.th, className: "mono" },
      { id: "trial", header: t("geneSearch.trial"), value: (r) => r.kis, className: "mono" },
      {
        id: "plot",
        header: t("geneSearch.plot"),
        value: (r) => r.parc,
        text: (r) => (r.alp ? `${r.parc}/${r.alp}` : String(r.parc)),
        numeric: true,
        className: "mono",
      },
      lineColumn("genotype", t("geneSearch.genotype"), (r) => r.line ?? { key: r.key, name: null }),
      keyColumn((r) => r.key),
      ...(data?.fields ?? []).map(
        (f, i): Column<TraitRow> => ({
          id: `field:${f}`,
          header: labels.get(f) ?? f,
          value: (r) => r.values[i],
          text: (r) => cell(r.values[i] ?? null),
          // a text field (a Display field such as a remark) reads left to right
          numeric: !data?.rows.some((r) => typeof r.values[i] === "string"),
        }),
      ),
      ...termColumns<TraitRow>(terms, (r) => r.results),
      // no gene asked: every lab result of the plot's line
      ...(terms.length
        ? []
        : [
            {
              id: "molecular",
              header: t("geneSearch.tab.molecular"),
              value: (r: TraitRow) => (r.molecular ?? []).join("; "),
              className: "sm",
            },
          ]),
      { id: "group", header: t("geneSearch.group"), value: (r) => label(r.klass), className: "sm" },
    ];
  }, [t, data, fields, terms, label]);
  const shown = useMemo(() => data?.rows.slice(0, TRAIT_SHOWN), [data]);
  const table = useDataTable("genealogy.gene-search.trait.plots", columns, shown, { partial: !!data && data.total > (shown?.length ?? 0) });
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="hstack" style={{ gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
        <h3 className="sec grow">{t("geneSearch.perPlot")}</h3>
        <MultiSelect
          size="xs"
          w={320}
          searchable
          clearable
          maxValues={9}
          label={t("geneSearch.extraFields")}
          description={t("geneSearch.extraFieldsHelp")}
          placeholder={hint("searchSelect")}
          value={props.extra}
          onChange={props.onExtra}
          data={props.fields.filter((f) => f.value !== props.body.field).map((f) => ({ value: f.value, label: f.label }))}
          limit={100}
        />
        <SegmentedControl
          size="xs"
          value={props.order}
          onChange={(v) => props.onOrder(v as TraitInOrder)}
          data={[
            { value: "plot", label: t("geneSearch.order.plot") },
            { value: "line", label: t("geneSearch.order.line") },
          ]}
        />
        <Download
          url={getGeneSearchTraitExportUrl()}
          body={props.body}
          file={t("geneSearch.markerTraitFile")}
          label={t("geneSearch.excel")}
        />
      </div>
      {rows.isFetching && !data && <Loader size="sm" m="md" />}
      {rows.error != null && <p className="note warn sm">{failure(t, rows.error)}</p>}
      {data && (
        <div className="stack" style={{ maxHeight: 480 }}>
          <DataTable table={table} rowKey={(r) => `${r.ev}-${r.th}-${r.kis}-${r.parc}-${r.alp}`} />
        </div>
      )}
      {data && (
        <span className="sm mu">
          {t("geneSearch.plotCount", { count: formatNumber(data.total) })}
          {data.total > TRAIT_SHOWN && ` · ${t("geneSearch.plotsFirst", { count: formatNumber(TRAIT_SHOWN) })}`}
        </span>
      )}
    </div>
  );
}

function TraitTable({ groups, label }: { groups: TraitGroup[]; label: (k: TraitGroup["klass"]) => string }) {
  const { t } = useTranslation();
  const columns = useMemo<Column<TraitGroup>[]>(
    () => [
      { id: "group", header: t("geneSearch.group"), value: (g) => label(g.klass) },
      { id: "lines", header: t("geneSearch.lines"), value: (g) => g.lines, numeric: true },
      ...STATS.map(
        (k): Column<TraitGroup> => ({
          id: k,
          header: t(`geneSearch.stat.${k}`),
          value: (g) => g.summary?.[k],
          numeric: true,
          text: (g) => (g.summary ? formatNumber(g.summary[k], k === "n" ? 0 : 2) : "—"),
        }),
      ),
    ],
    [t, label],
  );
  const table = useDataTable("genealogy.gene-search.trait.groups", columns, groups);
  return <DataTable table={table} rowKey={(g) => g.klass} />;
}

function BoxPlot({ groups, label }: { groups: TraitGroup[]; label: (k: TraitGroup["klass"]) => string }) {
  const { t } = useTranslation();
  const shown = groups.flatMap((g) => (g.summary ? [{ ...g, s: g.summary }] : []));
  if (!shown.length) return <p className="sm mu">{t("geneSearch.noData")}</p>;
  const lo = Math.min(...shown.map((g) => g.s.minimum));
  const hi = Math.max(...shown.map((g) => g.s.maximum));
  const y = (v: number) => (hi === lo ? 50 : 100 - ((v - lo) / (hi - lo)) * 100);
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="gs-boxes">
        {shown.map(({ klass, s }) => (
          <svg
            key={klass}
            className={`gs-box ${klass}`}
            viewBox="0 0 40 100"
            preserveAspectRatio="none"
            role="img"
            aria-label={t("geneSearch.boxTooltip", {
              group: label(klass),
              median: formatNumber(s.median),
              q1: formatNumber(s.q1),
              q3: formatNumber(s.q3),
            })}
          >
            <line x1="20" x2="20" y1={y(s.maximum)} y2={y(s.minimum)} className="wh" />
            <rect x="6" width="28" y={y(s.q3)} height={Math.max(y(s.q1) - y(s.q3), 0.5)} className="bo" />
            <line x1="6" x2="34" y1={y(s.median)} y2={y(s.median)} className="md" />
          </svg>
        ))}
      </div>
      <div className="gs-boxes sm">
        {shown.map(({ klass, s, lines }) => (
          <span key={klass} className="gs-box-label">
            {label(klass)}
            <br />
            <b>{t("geneSearch.sampleSize", { count: formatNumber(s.n), line: formatNumber(lines) })}</b> ·{" "}
            {t("geneSearch.median", { m: formatNumber(s.median) })}
          </span>
        ))}
      </div>
    </div>
  );
}
